import { translationKey } from '../../i18n';
import { localize, readAppLanguage } from '../../i18n';
import Button from '../ui/FitButton';
import LoadingLabel from '../LoadingLabel';
import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SkipReasonDetails from '../SkipReasonDetails';
import SingleLineText from '../ui/SingleLineText';

const ROW_BG = {
    asNeeded: '#eff6ff',
    taken: '#f0fdf4',
    missed: '#fef2f2',
    pending: '#fafafa',
    skipped: '#fafafa',
};
const ROW_BORDER = {
    asNeeded: '#bfdbfe',
    taken: '#dcfce7',
    missed: '#fecaca',
    pending: '#f0f0f0',
    skipped: '#f0f0f0',
};

const BADGE = {
    asNeeded: { bg: '#dbeafe', fg: '#1d4ed8', label: translationKey('완료') },
    taken: { bg: '#22c55e', fg: '#fff', label: translationKey('완료') },
    missed: { bg: '#fff', fg: '#ef4444', border: '#ef4444', label: translationKey('놓침') },
    pending: { bg: '#f3f4f6', fg: '#6b7280', label: translationKey('예정') },
    skipped: { bg: '#e5e7eb', fg: '#525252', label: translationKey('건너뜀') },
};

function formatTimeKr(timeStr) {
    // "08:12" or "08:12:34" → "오전 8:12"
    const [hh, mm] = timeStr.split(':');
    const h = parseInt(hh, 10);
    const ampm = localize(h < 12 ? '오전' : '오후');
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${ampm} ${h12}:${mm}`;
}

export default function MedRow({
    logId, name, color, status, scheduledTime, confirmedAt, photoUrl,
    onDelete, deleting, deletable, onMarkTaken, onEditTime, processing,
    skipReasonLog,
}) {
    let meta;
    if (status === 'asNeeded' && confirmedAt) {
        meta = localize("{v0}에 필요해서 복용", { v0: formatTimeKr(confirmedAt.slice(11, 16)) });
    } else if (status === 'taken' && confirmedAt) {
        meta = localize("{v0}에 복용", { v0: formatTimeKr(confirmedAt.slice(11, 16)) });
    } else if (status === 'missed') {
        meta = translationKey('복용 안 함');
    } else if (status === 'pending') {
        meta = localize("{v0} 알림 예정", { v0: formatTimeKr(scheduledTime) });
    }

    const badge = BADGE[status];
    const canDelete = deletable && (status === 'taken' || status === 'asNeeded' || status === 'skipped' || status === 'missed');
    const canMarkTaken = (status === 'missed' || status === 'skipped' || status === 'pending') && !!onMarkTaken;
    const canEditTime = status === 'taken' && !!onEditTime;

    return (
        <Box
            data-log-id={logId}
            sx={{
                position: 'relative',
                padding: '10px 12px 10px 10px',
                bgcolor: ROW_BG[status],
                border: `1px solid ${ROW_BORDER[status]}`,
                boxShadow: status === 'asNeeded' ? 'inset 0 0 0 1px #60a5fa' : 'none',
                borderRadius: '12px',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '3px',
                    backgroundColor: color.solid,
                },
            }}
        >
            <Box data-calendar-record-heading sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            {photoUrl ? (
                <Box
                    component="img"
                    src={photoUrl}
                    alt=""
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '9px',
                        objectFit: 'cover',
                        border: '1px solid rgba(0,0,0,0.06)',
                        flexShrink: 0,
                        marginLeft: '4px',
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: color.solid,
                        flexShrink: 0,
                        marginLeft: '4px',
                    }}
                />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    data-calendar-record-name
                    sx={{
                        fontSize: '1rem',
                        fontWeight: 700,
                        lineHeight: 1.4,
                        letterSpacing: '-0.02em',
                        color: '#1a1a1a',
                        wordBreak: 'keep-all',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {name}
                </Typography>
                {meta && (
                    <SingleLineText sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#888', mt: '2px' }}>
                        {localize(meta)}
                    </SingleLineText>
                )}
            </Box>
            </Box>
            <SkipReasonDetails log={skipReasonLog} />
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            <Box
                sx={{
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    padding: '4px 8px',
                    borderRadius: '999px',
                    bgcolor: badge.bg,
                    color: badge.fg,
                    border: badge.border ? `1px solid ${badge.border}` : 'none',
                    flexShrink: 0,
                    maxWidth: readAppLanguage() === 'ko' ? 72 : '100%',
                }}
            >
                <SingleLineText component="span" sx={{ display: 'block', font: 'inherit' }}>{localize(badge.label)}</SingleLineText>
            </Box>
            {(canMarkTaken || canEditTime || canDelete) && <Box data-calendar-record-actions sx={{
                display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1,
                flex: `1 1 ${canMarkTaken || canEditTime ? 156 : 44}px`,
                minWidth: canMarkTaken || canEditTime ? 156 : 44,
            }}>
            {(canMarkTaken || canEditTime) && (
                <Button
                    size="small"
                    disabled={processing}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (canMarkTaken) onMarkTaken();
                        else onEditTime();
                    }}
                    sx={{
                        minWidth: 0,
                        minHeight: 44,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: '10px',
                        fontSize: '0.875rem',
                        fontWeight: 800,
                        color: canMarkTaken ? '#5a4a00' : '#15803d',
                        bgcolor: canMarkTaken ? '#ffe566' : '#dcfce7',
                        whiteSpace: 'nowrap',
                        flexShrink: 1,
                        '&:hover': { bgcolor: canMarkTaken ? '#f5d94f' : '#bbf7d0' },
                        '&:disabled': { bgcolor: '#e5e7eb', color: '#999' },
                    }}
                >
                    {processing ? <LoadingLabel>{null}</LoadingLabel> : canMarkTaken ? localize('복용으로 변경') : localize('시간 수정')}
                </Button>
            )}
            {canDelete && (
                <IconButton
                    aria-label={localize("{v0} 기록 삭제", { v0: name })}
                    disabled={deleting}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete && onDelete();
                    }}
                    sx={{
                        width: 44,
                        height: 44,
                        bgcolor: '#fff7f4',
                        border: '1px solid #f4d6cc',
                        borderRadius: '10px',
                        flexShrink: 0,
                        opacity: deleting ? 0.5 : 1,
                        '&:hover': { bgcolor: '#ffefe9', borderColor: '#edc2b3' },
                        '&:active': { bgcolor: '#ffe7df', transform: 'scale(0.96)' },
                    }}
                >
                    {deleting ? <LoadingLabel>{null}</LoadingLabel> : <DeleteOutlineIcon sx={{ fontSize: 18, color: '#c65a3a' }} />}
                </IconButton>
            )}
            </Box>}
            </Box>
        </Box>
    );
}
